import React from 'react';
import { MoreHorizontal, Download, RotateCcw, Trash2 } from 'lucide-react';
import VersionStatusBadge from './VersionStatusBadge';
import type { GameVersion } from '../../types/api';

interface VersionListItemProps {
  version: GameVersion;
  onActionSelect: (action: VersionAction, version: GameVersion) => void;
  showActions?: boolean;
  className?: string;
}

export type VersionAction = 'set-current' | 'delete' | 'download';

// Helper function to format file size
const formatFileSize = (sizeStr: string | number | undefined) => {
  if (!sizeStr) return 'N/A';
  const size = typeof sizeStr === 'string' ? parseInt(sizeStr) : sizeStr;
  if (isNaN(size)) return 'N/A';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Helper function to format time
const formatTimeAgo = (dateString: string | undefined) => {
  if (!dateString) return 'Unknown date';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid date';
  
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const VersionListItem: React.FC<VersionListItemProps> = ({
  version,
  onActionSelect,
  showActions = true,
  className = ''
}) => {
  const [showActionsDropdown, setShowActionsDropdown] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowActionsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleActionClick = (action: VersionAction) => {
    setShowActionsDropdown(false);
    onActionSelect(action, version);
  };

  const getActionItems = () => {
    const items = [
      {
        action: 'download' as VersionAction,
        label: 'Download',
        icon: <Download className="w-4 h-4" />,
        disabled: version.upload_status !== 'completed'
      },
      {
        action: 'set-current' as VersionAction,
        label: 'Set as Current',
        icon: <RotateCcw className="w-4 h-4" />,
        disabled: version.is_current || version.upload_status !== 'completed'
      },
      {
        action: 'delete' as VersionAction,
        label: 'Delete',
        icon: <Trash2 className="w-4 h-4" />,
        disabled: version.is_current
      }
    ];

    return items;
  };

  return (
    <tr className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${className}`}>
      {/* Version */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col">
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
            v{version.version_number}
          </div>
          <div className="text-xs text-gray-500 dark:text-dark-text-muted">
            #{version.bundle_version}
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <VersionStatusBadge
          status={version.upload_status}
          isCurrent={version.is_current}
        />
      </td>

      {/* Size */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
        {formatFileSize(version.total_size)}
      </td>

      {/* Files */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
        {version.file_count}
      </td>

      {/* Uploaded */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col">
          <div className="text-sm text-gray-900 dark:text-dark-text">
            {formatTimeAgo(version.uploaded_at)}
          </div>
          <div className="text-xs text-gray-500 dark:text-dark-text-muted">
            by {version.uploaded_by?.username || 'Unknown'}
          </div>
        </div>
      </td>

      {/* Changelog */}
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900 dark:text-dark-text max-w-xs truncate">
          {version.changelog || version.upload_notes || 'No changelog'}
        </div>
      </td>

      {/* Actions */}
      {showActions && (
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>

            {showActionsDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="py-1">
                  {getActionItems().map((item) => (
                    <button
                      key={item.action}
                      onClick={() => handleActionClick(item.action)}
                      disabled={item.disabled}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                        item.disabled
                          ? 'text-gray-400 dark:text-gray-500'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${
                        item.action === 'delete' && !item.disabled
                          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10'
                          : ''
                      }`}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default VersionListItem; 